import { DataGrid, GridCellParams, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import React, { useState } from 'react';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { OverlayDisplayDiv } from '../../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import {
  approveInventoryReports,
  formatCustomDate,
  getTransactions,
  deleteRow,
  MaterialTransaction,
} from './transactionGridUtils';
import { REPORTING_TIME } from '../../../../utils/gridUtils';
import { CircularProgress } from '@mui/material';
import { getUnitObj } from '../../../../utils/MassUnits/massUnitsUtils';
import { IInventoryReportType } from '../../../../sp-types/InventoryReportType';
import { Material } from '../../../../sp-types';

interface Props {
  setIsShowUnderwriting: (_value: boolean) => void;
  underWritingInventoryReports?: IInventoryReportType[] | null;
  colorDictionary: Record<string, string>;
  materialsMap: Record<string, Material>;
  refetchInventory: () => void;
}

export const UnderWritingTransactionGrid: React.FC<Props> = ({
  setIsShowUnderwriting,
  refetchInventory,
  underWritingInventoryReports,
  colorDictionary,
  materialsMap,
}) => {
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const apiRef = useGridApiRef();

  const transactionGridBaseColumns: Partial<GridColDef> = { headerClassName: 'bg-gray-200 ', width: 150, flex: 1 };

  const transactionGridColumns: GridColDef[] = [
    {
      field: 'materialName',
      headerName: 'Material',
      cellClassName: 'p-0  border ',
      headerClassName: 'bg-gray-200  border',
      renderCell: (params: GridCellParams) => {
        return (
          <div className="border-l-8 pl-2" style={{ borderLeftColor: params.row.color }}>
            {params.row.materialName}
          </div>
        );
      },
    },

    {
      field: REPORTING_TIME,
      headerName: 'Date',
      cellClassName: 'border ',
      type: 'date',
      renderCell: (params: GridCellParams) => {
        const { reportingTime } = params.row as MaterialTransaction;
        return reportingTime ? formatCustomDate(reportingTime?.toDate()) : '';
      },
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      cellClassName: 'border text-left',
      renderCell: ({ row }: GridCellParams): React.ReactNode => (
        <div>
          {row.quantity} {getUnitObj(materialsMap[row.materialId]?.unit).shortForm}
        </div>
      ),
      type: 'string',
    },

    {
      field: 'action',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 32,
      maxWidth: 48,
      cellClassName: 'border w-[1rem]',
      renderCell: (params: GridCellParams) => {
        const row = params.row as MaterialTransaction;
        const isLoading = loadingRows[row.id || ''];
        return (
          <button
            onClick={() => deleteRow(row, refetchInventory, setLoadingRows)}
            className="   h-full flex flex-1  items-center"
          >
            {isLoading ? (
              <CircularProgress size={24} className="text-blue-600 " />
            ) : (
              <img className="h-6 w-6 " src="./assets/icons/blue-flat/delete.svg" alt="Delete" />
            )}
          </button>
        );
      },
    },
  ];

  return (
    <OverlayDisplayDiv setDisplayOverlay={setIsShowUnderwriting}>
      <div className=" grid-container w-[50rem] p-8 bg-white space-y-6  ">
        <h6 className="text-lg">Inventory reports waiting for approval</h6>
        <DataGrid
          apiRef={apiRef}
          rows={getTransactions(underWritingInventoryReports, colorDictionary, materialsMap, '')}
          columns={transactionGridColumns.map((col) => ({ ...transactionGridBaseColumns, ...col }))}
          loading={!underWritingInventoryReports}
          pageSizeOptions={[50]}
          paginationMode="server"
          onPaginationModelChange={(val) => console.log('page change', val)}
          rowHeight={32}
          columnHeaderHeight={32}
          hideFooterPagination
        />

        <div className="flex flex-row gap-2">
          <ButtonDesigned
            style="outline"
            onButtonClick={() => {
              setIsShowUnderwriting(false);
            }}
            className="py-2 px-3"
          >
            Cancel
          </ButtonDesigned>
          <ButtonDesigned
            className="py-2 px-4"
            onButtonClick={async () => {
              await approveInventoryReports(underWritingInventoryReports || [], () => setIsShowUnderwriting(false));
              await refetchInventory();
            }}
          >
            Approve
          </ButtonDesigned>
        </div>
      </div>
    </OverlayDisplayDiv>
  );
};
